import { Button, Select } from '@innovamat/glimmer-components';
import { useOrganizationQuery } from '@innovamat/glow-api-client';
import { useOnClickOutside } from '@innovamat/hooks';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useUser } from '../../../../../user-management';
import { useAssignTeachersToClassroom } from '../../../../hooks/use-assign-teachers-to-classroom';
import { useClassrooms } from '../../../../hooks/use-classrooms';
import { Classroom } from '../../../../modules/classroom';
import type { OrganizationTeacher } from '../../../../modules/teacher';

type Props = {
  teacher: OrganizationTeacher | undefined;
  onSuccess: (classroom: Classroom) => void;
  displayClassrooms: Classroom[];
};

function AssignClassroomToTeacherButton({
  teacher,
  onSuccess,
  displayClassrooms,
}: Props): JSX.Element {
  const { user } = useUser();
  const { t } = useTranslation();
  const { data: organizationQuery } = useOrganizationQuery(
    { id: user?.organizationId! },
    { staleTime: Infinity, enabled: !!user?.organizationId }
  );
  const [showInput, setShowInput] = useState(false);
  const ref = useRef(null);

  const { handleAssignTeacherToClassrooms } = useAssignTeachersToClassroom({
    organization: organizationQuery?.organization!,
    onSuccess: (data) => {
      const classroom = classrooms?.find(
        (classroom) => classroom.id === data.classroomId
      );
      if (!classroom) return;
      onSuccess(classroom);
    },
  });

  const { classrooms } = useClassrooms({ mine: false });

  const handleSelectClassroom = (classroomId: string): void => {
    if (!teacher) return;
    handleAssignTeacherToClassrooms({
      classroomId: classroomId,
      classroomName: classroomId,
      emails: [teacher.email],
    });

    setShowInput(false);
  };

  const filterAlreadyAssignedClassrooms = (classroom: Classroom) => {
    return !displayClassrooms?.some(
      (teacherClassroom) => teacherClassroom?.id === classroom.id
    );
  };

  const classroomToOption = (classroom: Classroom) => {
    return {
      label: classroom.name!,
      value: classroom.id,
    };
  };

  const classroomOptions = useMemo(() => {
    return classrooms
      .filter(filterAlreadyAssignedClassrooms)
      .map(classroomToOption);
  }, [classrooms, displayClassrooms]);

  useEffect(() => {
    setShowInput(false);
  }, [teacher]);

  const handleShowInput = (): void => {
    setShowInput(true);
  };

  useOnClickOutside(ref, () => {
    if (showInput) {
      setShowInput(false);
    }
  });

  if (showInput) {
    return (
      <div ref={ref}>
        <Select
          defaultMenuIsOpen
          leftIcon="SearchIcon"
          isSearchable
          onChange={(option) =>
            option?.value && handleSelectClassroom(option?.value)
          }
          options={classroomOptions}
          placeholder={t('classroom.input.label.classname')}
          dataTestId="assign-classroom-select"
        />
      </div>
    );
  }

  return (
    <Button
      fill
      variant="secondary"
      onClick={handleShowInput}
      dataTestId="assign-classroom-button"
    >
      {t('manager.assign-classroom')}
    </Button>
  );
}

export { AssignClassroomToTeacherButton };
